import React, { useState } from 'react';
import { Avatar, Button, HStack, Text, VStack, useToast } from '@chakra-ui/react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { getInfluencerByInstagramHandle, getInfluencerByTikTokHandle } from '../../../services/firebaseService';
import { Influencer } from '@foodfluence/shared';
import ModalShell from '../../../components/ui/ModalShell';
import { Pill, PillSearch } from '../../../components/ui';
import { ff } from '../../../theme';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: FaInstagram },
  { value: 'tiktok', label: 'TikTok', icon: FaTiktok },
] as const;

interface SwitchAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchAccount: (influencer: Influencer) => void;
}

const SwitchAccountModal: React.FC<SwitchAccountModalProps> = ({
  isOpen,
  onClose,
  onSwitchAccount
}) => {
  const [platform, setPlatform] = useState<'instagram' | 'tiktok'>('instagram');
  const [handle, setHandle] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [foundInfluencer, setFoundInfluencer] = useState<Influencer | null>(null);
  const toast = useToast();

  const handleSearch = async () => {
    if (!handle.trim()) {
      toast({
        title: 'Please enter a handle',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearching(true);
    setFoundInfluencer(null);

    try {
      const cleanHandle = handle.replace('@', '').trim();
      let influencer: Influencer | null = null;

      if (platform === 'instagram') {
        influencer = await getInfluencerByInstagramHandle(cleanHandle);
      } else {
        influencer = await getInfluencerByTikTokHandle(cleanHandle);
      }

      if (influencer) {
        setFoundInfluencer(influencer);
      } else {
        toast({
          title: 'User not found',
          description: `No user found with ${platform === 'instagram' ? 'Instagram' : 'TikTok'} handle @${cleanHandle}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error searching for influencer:', error);
      toast({
        title: 'Error',
        description: 'Failed to search for user. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSwitch = () => {
    if (foundInfluencer) {
      onSwitchAccount(foundInfluencer);
      handleClose();
    }
  };

  const handleClose = () => {
    setHandle('');
    setFoundInfluencer(null);
    setPlatform('instagram');
    onClose();
  };

  const platformLabel = platform === 'instagram' ? 'Instagram' : 'TikTok';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Switch account"
      cancelLabel="Cancel"
      primary={
        foundInfluencer
          ? { label: 'Switch account', onClick: handleSwitch, arrow: true }
          : {
              label: 'Search',
              onClick: handleSearch,
              isLoading: isSearching,
              loadingText: 'Searching...',
              isDisabled: !handle.trim(),
            }
      }
    >
      <VStack spacing={4} align="stretch">
        <HStack spacing={2}>
          {PLATFORMS.map(({ value, label, icon: PlatformIcon }) => {
            const active = platform === value;
            return (
              <Button
                key={value}
                leftIcon={<PlatformIcon />}
                h="40px"
                px={4}
                borderRadius="full"
                fontSize="14px"
                fontWeight={600}
                bg={active ? ff.ink : 'white'}
                color={active ? 'white' : ff.ink}
                boxShadow={active ? 'none' : `inset 0 0 0 1px ${ff.border}`}
                _hover={{ bg: active ? ff.ink : ff.grey }}
                aria-pressed={active}
                onClick={() => {
                  setPlatform(value);
                  setFoundInfluencer(null);
                }}
              >
                {label}
              </Button>
            );
          })}
        </HStack>

        <PillSearch
          autoFocus
          aria-label={`${platformLabel} handle`}
          placeholder={`${platformLabel} @username`}
          value={handle}
          onChange={(e) => {
            setHandle(e.target.value);
            if (foundInfluencer) setFoundInfluencer(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isSearching && handle.trim()) handleSearch();
          }}
        />

        {foundInfluencer && (
          <HStack
            as="button"
            type="button"
            onClick={handleSwitch}
            spacing={3}
            p={3}
            borderRadius="20px"
            bg={ff.limeSurface}
            boxShadow={`inset 0 0 0 1.5px ${ff.lime}`}
            textAlign="left"
            w="100%"
          >
            <Avatar src={foundInfluencer.profile_pic_url} name={foundInfluencer.firstName} size="md" />
            <VStack align="start" spacing={0} flex={1} minW={0}>
              <Text fontWeight={600} fontSize="15px" color={ff.ink} noOfLines={1}>
                {foundInfluencer.firstName} {foundInfluencer.lastName}
              </Text>
              <HStack spacing={3} color={ff.text2} fontSize="13px">
                {foundInfluencer.instagramHandle && (
                  <HStack spacing={1}>
                    <FaInstagram size={12} />
                    <Text>@{foundInfluencer.instagramHandle}</Text>
                  </HStack>
                )}
                {foundInfluencer.tiktokHandle && (
                  <HStack spacing={1}>
                    <FaTiktok size={12} />
                    <Text>@{foundInfluencer.tiktokHandle}</Text>
                  </HStack>
                )}
              </HStack>
            </VStack>
            <Pill tone="ink">Switch</Pill>
          </HStack>
        )}
      </VStack>
    </ModalShell>
  );
};

export default SwitchAccountModal;
